import type { GetContractBillingScheduleReportResponse } from '@weft/api-definition';
import type { Currency, Months, Product, RevenuePerMonth } from '@weft/types';
import { entries, flatExtract, isEmptyObject, isNotEmptyObject, sumBy, uniqueBy, validateNotNullOrUndefined, values } from '@weft/utils';
import React from 'react';
import { VyTable } from '../../../../components/new-design/table/vy-table/VyTable';
import { withSuspenseBoundary } from '../../../../components/utility/SuspenseBoundary';
import { useContractBillingScheduleReport } from '../../../../hooks/requests/use-contract-billing-schedule-report';
import type { ContractBillingScheduleColumns } from './contract-page-billing-schedule-utils/get-contract-page-billing-schedule-columns';
import { getContractPageBillingScheduleColumns } from './contract-page-billing-schedule-utils/get-contract-page-billing-schedule-columns';

type ContractPageBillingScheduleProps = {
  contractId: string;
  products: Product[];
  currency: Currency;
  onLoadData?: () => void;
};

const ContractPageBillingSchedule: React.FC<ContractPageBillingScheduleProps> = ({ currency, contractId, products, onLoadData }) => {
  const onContractBillingScheduleReportSuccess = (successData?: GetContractBillingScheduleReportResponse) => {
    if (isNotEmptyObject(successData)) {
      onLoadData?.();
    }
  };

  const { data } = useContractBillingScheduleReport(contractId, { suspense: true, onSuccess: (successData) => onContractBillingScheduleReportSuccess(successData) });
  validateNotNullOrUndefined(data);

  const uniqueColumnKeys = getUniqueColumnKeys(data.productVariantsRevenues);
  const columns = getContractPageBillingScheduleColumns(uniqueColumnKeys, currency);
  const rows = buildRowData(data, products);

  if (isEmptyObject(data.productVariantsRevenues)) {
    return null;
  }

  return (
    <div className="mt-4">
      <VyTable
        itemsCount={0}
        items={rows}
        columns={columns}
        sx={{
          maxHeight: '340px',
          border: '1px solid #E5E5E5',
        }}
        showPagination={false}
      />
    </div>
  );
};

function getUniqueColumnKeys(productVariantsRevenues: GetContractBillingScheduleReportResponse['productVariantsRevenues']): { month: Months, year: string }[] {
  const allYearlyRevenues = values(productVariantsRevenues);
  const allKeys = allYearlyRevenues.flatMap((yearlyRevenues) => getRevenuesPerMonthKeys(yearlyRevenues));

  return uniqueBy([...allKeys], ({ month, year }) => `${month}-${year}`);
}

function getRevenuesPerMonthKeys(yearRevenuesPerMonth: Record<string | number, RevenuePerMonth>): { month: Months, year: string }[] {
  return entries(yearRevenuesPerMonth).flatMap(([year, revenuePerMonth]) => getRevenuePerMonthKeys(year.toString(), revenuePerMonth));
}

function getRevenuePerMonthKeys(year: string, revenuePerMonth: RevenuePerMonth): { month: Months, year: string }[] {
  return entries(revenuePerMonth).map(([month, _]) => ({ month, year }));
}

function buildRowData(data: GetContractBillingScheduleReportResponse, products: Product[]): ContractBillingScheduleColumns[] {
  const allVariants = flatExtract(products, 'variants');

  return entries(data.productVariantsRevenues).map<ContractBillingScheduleColumns>(([variantId, yearlyRevenue]) => {
    const revenuesPerMonth = values(yearlyRevenue);
    const amount = sumBy(revenuesPerMonth, (revenue) => sumBy(values(revenue), ({ scheduledCharge }) => scheduledCharge));
    const item = allVariants.find((variant) => variant.id === variantId)?.name ?? '';
    
    return {
      id: variantId,
      item,
      amount,
      yearlyRevenue,
    };
  });
}

const ContractPageBillingScheduleWithSuspenseBoundary = withSuspenseBoundary(ContractPageBillingSchedule);
export { ContractPageBillingScheduleWithSuspenseBoundary as ContractPageBillingSchedule };